"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import clsx from "clsx"
import { IconButton } from "@/elements"
import { XMarkIcon } from "@heroicons/react/24/outline"
import { cn } from "@/utils/classnames"
import { VisuallyHidden } from "@radix-ui/react-visually-hidden"
import { IconButtonVariant } from "@/elements/IconButton/useIconButtonStyles"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

interface DialogOverlayProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> {
  zIndex?: number
}

const DialogOverlay = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Overlay>, DialogOverlayProps>(
  ({ className, zIndex = 100, ...props }, ref) => (
    <DialogPrimitive.Overlay
      ref={ref}
      style={{
        zIndex,
      }}
      className={clsx(
        "fixed inset-0 bg-overlay data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      )}
      onClick={e => {
        e.preventDefault()
        e.stopPropagation()
        props.onClick?.(e)
      }}
      {...props}
    />
  )
)
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  fullScreenOnMobile?: boolean
  zIndex?: number
  hasMaxWidth?: boolean
  closeIconButtonVariant?: IconButtonVariant
  onClose?: (e: React.MouseEvent<HTMLDivElement, MouseEvent> | React.KeyboardEvent<HTMLDivElement>) => void
  withClose?: boolean
}

const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, DialogContentProps>(
  (
    {
      className,
      children,
      fullScreenOnMobile = true,
      zIndex = 101,
      onClose,
      closeIconButtonVariant = "white",
      hasMaxWidth = true,
      withClose = true,
      ...props
    },
    ref
  ) => (
    <DialogPortal>
      <VisuallyHidden>
        <DialogTitle />
      </VisuallyHidden>
      <DialogOverlay zIndex={zIndex - 1} />
      <DialogPrimitive.Content
        style={{
          zIndex,
          boxShadow: "0px 8px 25px 0px rgba(0, 0, 0, 0.20)",
        }}
        ref={ref}
        className={cn(
          "dialog fixed left-[50%] top-[50%] grid w-full translate-x-[-50%] translate-y-[-50%] !outline-none focus:outline-none focus:ring-0 focus-visible:outline-none",
          "border-none bg-bg-1 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
          fullScreenOnMobile
            ? "h-full w-full rounded-none sm:h-auto sm:w-auto sm:rounded-xl"
            : "max-w-[calc(100vw-40px)] rounded-xl",
          "md:rounded-xl",
          "overflow-hidden",
          hasMaxWidth && "sm:max-w-[500px] lg:max-w-[540px]",
          className
        )}
        {...props}>
        <div className="flex h-full max-h-dvh flex-col">{children}</div>
        {withClose && (
          <DialogPrimitive.Close className="data-[state=open]:bg-accent data-[state=open]:text-muted-foreground fixed right-6 top-6 z-10 ring-offset-bg-1 focus:outline-none focus:ring-0 disabled:pointer-events-none">
            <IconButton
              Icon={XMarkIcon}
              onClick={e => {
                if (onClose) {
                  e.preventDefault()
                  e.stopPropagation()
                  onClose(e)
                }
              }}
              variant={closeIconButtonVariant}
            />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={clsx("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={clsx("text-muted-foreground text-sm", className)} {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogTitle,
  DialogDescription,
}
